import { Stack } from "@mui/material";
import { Badge, Button, FormLayout, Link, Modal, Text, TextField } from "@shopify/polaris";
import React, { useCallback, useState } from "react";

import { IntegrationBrands, IntegrationLogo } from "./integrationLogos";
import { AccountStatus, CardInner, ManualIntegrationCard, ModalWrapper } from "./styles";
import { IntegrationSectionProps } from "./types";

export function KlaviyoLegacy({
  onDelete,
  onChange,
  onSave,
  loading,
  value,
  savedValue,
}: IntegrationSectionProps): JSX.Element {
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const modalChange = useCallback(() => setActive(!active), [active]);
  const connected = (value?.apiKey && value?.secretApiKey) || value?.apiKey === "CONNECTED";
  const connectedAndSaved =
    (savedValue?.apiKey && savedValue?.secretApiKey) || savedValue?.apiKey === "CONNECTED";
  const title = connectedAndSaved ? "Disconnect Klaviyo" : "Connect Klaviyo";

  return (
    <ManualIntegrationCard data-testid="klaviyo-legacy-component">
      <ModalWrapper>
        <Modal
          open={!active}
          onClose={modalChange}
          title={title}
          primaryAction={{
            content: "Confirm",
            loading: loading || busy,
            disabled: !connected,
            onAction() {
              if (connectedAndSaved) {
                onDelete();
                modalChange();
              } else {
                onSave();
                modalChange();
              }
            },
          }}
          secondaryActions={[
            {
              content: "Cancel",
              loading,
              onAction: modalChange,
            },
          ]}
        >
          <Modal.Section>
            {connectedAndSaved ? (
              <p data-testid="disconnect-description">
                Disconnecting your Klaviyo account will stop the flow of subscription event data from Smartrr to
                Klaviyo. This means that any email or SMS flows in Klaviyo that rely on this data will no longer
                be triggered by Smartrr events. You can reconnect your account at any time. Are you sure you want
                to disconnect?
              </p>
            ) : (
              <React.Fragment>
                <FormLayout>
                  <TextField
                    autoComplete="off"
                    label="Public API key"
                    value={value?.apiKey}
                    onChange={apiKey => onChange(apiKey, value?.secretApiKey)}
                  />
                  <TextField
                    autoComplete="off"
                    label="Private API key"
                    value={value?.secretApiKey}
                    onChange={secretApiKey => onChange(value?.apiKey ?? "", secretApiKey)}
                    type="password"
                    focused={!!(savedValue?.apiKey && !savedValue.secretApiKey)}
                  />
                  <Link
                    url="https://help.klaviyo.com/hc/en-us/articles/115005062267"
                    target="_blank"
                    accessibilityLabel="Klaviyo how to manage your account's API keys link"
                    removeUnderline
                  >
                    Where do I find my Klaviyo API keys?
                  </Link>
                </FormLayout>
              </React.Fragment>
            )}
          </Modal.Section>
        </Modal>
      </ModalWrapper>
      <CardInner>
        <CardInner>
          <Stack direction="column" spacing="10px">
            <Stack direction="row" justifyContent="space-between" alignItems="self-start">
              <Stack direction="row" spacing="10px">
                <div className="integration-logo">
                  <IntegrationLogo brand={IntegrationBrands.Klaviyo} />
                </div>
                <Stack direction="column" spacing="3px">
                  <Text variant="headingMd" as="h2">
                    Klaviyo (Legacy)
                  </Text>
                  {connectedAndSaved ? (
                    <AccountStatus>Account connected</AccountStatus>
                  ) : (
                    <AccountStatus>No account connected</AccountStatus>
                  )}
                </Stack>
              </Stack>
              {connectedAndSaved ? (
                <Button onClick={modalChange}>Disconnect</Button>
              ) : (
                <Button primary onClick={modalChange}>
                  Connect
                </Button>
              )}
            </Stack>
            <div className="integration-description">
              <b>Deprecated by Klaviyo</b> Connect to sync subscription event data to Klaviyo to set up email and
              SMS flows and notifications.
            </div>
            <div className="integration-attributes">
              <Badge>Emails</Badge>
              <Badge>SMS</Badge>
            </div>
            <div className="integration-more-info">
              <a
                target="_blank"
                href="https://help.smartrr.com/docs/support/integrations/klaviyo-x-smartrr-integration"
                rel="noreferrer"
              >
                More Info
              </a>
            </div>
          </Stack>
        </CardInner>
      </CardInner>
    </ManualIntegrationCard>
  );
}
